import React, { ReactElement, use, useEffect } from 'react';
import { Controller, FieldValues, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { BusinessOutlined } from '@mui/icons-material';
import { Button, Checkbox, InputAdornment, MenuItem, TextField, Typography } from '@mui/material';
import { GridColDef, GridRenderEditCellParams } from '@mui/x-data-grid';
import BreadCrumb from 'shared/components/bread-crumb/bread-crumb/bread-crumb';
import ConfirmationPopupProviderContext from 'shared/components/confirmation-pop-up/providers/contexts/confirmation-popup-provider-context';
import DataGrid, { DataGridOverflowMenuItem } from 'shared/components/data-grid/data-grid';
import SelectField from 'shared/components/formFields/select-field';
import LoadingIndicator from 'shared/components/loading-indicator/loading-indicator';
import PageHeader from 'shared/components/page-header/page-header';
import PopUp from 'shared/components/pop-up/pop-up';
import { RegexExpressions } from 'shared/constants/regex/regex-expressions';

import { useExplanationLinksContextProvider } from 'features/explanation-links/context/explanation-links-provider';
import ExplanationLink from 'features/explanation-links/domain/models/explanationLink';
import ExplanationLinkCommand from 'features/explanation-links/domain/models/explanationLink-command';
import OrganisationUnit from 'features/explanation-links/domain/models/organisationUnit';
import SettingTabs from 'features/settings/settings-tabs';

import 'features/explanation-links/views/explanation-links.scss';

const ExplanationLinks = (): React.JSX.Element => {
  const { t } = useTranslation('explanationLinks');
  const { enablePopUpGuard } = use(ConfirmationPopupProviderContext);

  const registerOptions = {
    title: {
      required: t('info.requiredHelperText'),
    },
    uri: {
      required: t('info.requiredHelperText'),
      validate: (value: string) => {
        const regex = new RegExp(RegexExpressions.URL);
        return regex.test(value.trim()) || t('info.uriFormatHelperText');
      },
    },
    organisationUnits: {
      required: t('info.requiredHelperText'),
    },
  };

  const {
    viewingMode,
    mutationIsLoading,
    currentSelectedExplanationLink,

    explanationLinks,
    explanationLinksResponseIsLoading,
    explanationLinksResponseIsSuccess,

    rowModesModel,
    handleAddExplanationLink,
    handleEditClick,
    handleSaveClick,
    handleCancelEdit,

    handleDeleteExplanationLink,
    isDeleteConfirmationPopupOpen,
    closeDeleteConfirmationPopup,
    deleteConfirmationAction,

    readOrganisationUnitsForSettingsData,
    readOrganisationUnitsForSettingsIsSuccess,
    organisationUnits,
  } = useExplanationLinksContextProvider();

  const defaultValues = {
    title: '',
    uri: '',
    organisationUnits: organisationUnits,
  };

  const form = useForm({ mode: 'onBlur', defaultValues });

  const control = form.control;
  const errors = form.formState.errors;
  const organisationUnitsWatch = form.watch('organisationUnits');

  useEffect(() => {
    form.reset({
      title: currentSelectedExplanationLink?.title ?? '',
      uri: currentSelectedExplanationLink?.uri ?? '',
      organisationUnits: organisationUnits,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentSelectedExplanationLink, organisationUnits]);

  useEffect(() => {
    enablePopUpGuard(form.formState.isDirty);
  }, [form.formState.isDirty]);

  const submitForm = (fieldValues: FieldValues): void => {
    const explanationLink: ExplanationLinkCommand = {
      id: currentSelectedExplanationLink?.id,
      title: fieldValues.title,
      uri: fieldValues.uri,
      organisationUnitIds: fieldValues.organisationUnits,
    };

    handleSaveClick(explanationLink);
  };

  const renderEditTitle = (props: GridRenderEditCellParams): ReactElement => (
    <Controller
      name="title"
      rules={registerOptions.title}
      control={control}
      render={({ field }) => (
        <TextField
          {...field}
          id="title"
          label={t('edit.title')}
          variant="outlined"
          error={!!errors.title}
          helperText={errors.title?.message?.toString()}
          inputRef={(input: HTMLInputElement) => {
            if (props.hasFocus) {
              input && input.focus();
            }
          }}
        />
      )}
    />
  );

  const renderEditUri = (props: GridRenderEditCellParams): ReactElement => (
    <Controller
      name="uri"
      rules={registerOptions.uri}
      control={control}
      render={({ field }) => (
        <TextField
          {...field}
          id="uri"
          label={t('edit.url')}
          variant="outlined"
          error={!!errors.uri}
          helperText={errors.uri?.message?.toString()}
          inputRef={(input: HTMLInputElement) => {
            if (props.hasFocus) {
              input && input.focus();
            }
          }}
        />
      )}
    />
  );

  const getNames = (selected: any): string => {
    const selectedNames: Array<string> = [];
    const selectedIds = selected as Array<string>;

    if (selectedIds !== null) {
      selectedIds.forEach((item: string) => {
        const organisation = readOrganisationUnitsForSettingsData?.find((organisationUnit) => organisationUnit.id === item);
        if (organisation) {
          selectedNames.push(organisation.name);
        }
      });
    }
    return selectedNames.join(', ');
  };

  const renderEditOrganisationUnit = (props: GridRenderEditCellParams): ReactElement => (
    <Controller
      name="organisationUnits"
      rules={registerOptions.organisationUnits}
      control={control}
      render={({ field }) => (
        <SelectField
          multiple
          {...field}
          id="organisationUnit"
          label={t('edit.organisationUnitAccess')}
          variant="outlined"
          error={!!errors.organisationUnits}
          helperText={errors?.organisationUnits?.message?.toString()}
          onClick={() => form.trigger('organisationUnits')}
          inputRef={(input: HTMLInputElement) => {
            if (props.hasFocus) {
              input && input.focus();
            }
          }}
          startAdornment={
            <InputAdornment position="start">
              <BusinessOutlined fontSize="small" />
            </InputAdornment>
          }
          renderValue={(selected: any) => getNames(selected)}
        >
          {readOrganisationUnitsForSettingsData &&
            readOrganisationUnitsForSettingsIsSuccess &&
            readOrganisationUnitsForSettingsData.map((organisationUnit) => (
              <MenuItem key={`organisation-unit-${organisationUnit.id}`} value={organisationUnit.id}>
                <Checkbox sx={{ padding: '0 9px' }} checked={organisationUnitsWatch.indexOf(organisationUnit.id) > -1} />
                {organisationUnit.name}
              </MenuItem>
            ))}
        </SelectField>
      )}
    />
  );

  const checkIsDisabled = (): boolean => viewingMode === 'editing' || viewingMode === 'creation';

  const getOverflowMenuItems = (): Array<DataGridOverflowMenuItem<ExplanationLink>> => {
    const menuItems: Array<DataGridOverflowMenuItem<ExplanationLink>> = [
      {
        label: t('settingsTable.action.edit'),
        action: (explanationLink) => handleEditClick(explanationLink),
        disabled: checkIsDisabled,
      },
      {
        label: 'divider',
      },
      {
        label: t('settingsTable.action.delete'),
        action: (explanationLink) => handleDeleteExplanationLink(explanationLink),
        disabled: checkIsDisabled,
      },
    ];

    return menuItems;
  };

  const getActionButtons = (): Array<ReactElement> => {
    const buttons: Array<ReactElement> = [
      <Button
        key={`Actionbutton_cancel${currentSelectedExplanationLink?.id}`}
        variant="text"
        onClick={() => handleCancelEdit(currentSelectedExplanationLink?.id ?? '')}
      >
        {t('edit.cancelFormButton')}
      </Button>,
      <Button
        key={`Actionbutton_save_${currentSelectedExplanationLink?.id}`}
        variant="contained"
        type="button"
        onClick={form.handleSubmit(submitForm)}
      >
        {t('edit.saveFormButton')}
      </Button>,
    ];

    return buttons;
  };

  const columns: GridColDef[] = [
    {
      field: 'title',
      headerName: t('settingsTable.column.title'),
      flex: 1,
      sortable: false,
      editable: true,
      renderCell: (params) => <Typography variant="subtitle1">{params.value}</Typography>,
      renderEditCell: renderEditTitle,
    },
    {
      field: 'uri',
      headerName: t('settingsTable.column.url'),
      flex: 1,
      sortable: false,
      editable: true,
      type: 'custom',
      renderCell: (params) => (
        <Typography variant="subtitle1">
          <a href={params.value} target="_blank" rel="noreferrer">
            {params.value}
          </a>
        </Typography>
      ),
      renderEditCell: renderEditUri,
    },
    {
      field: 'organisationUnits',
      headerName: t('settingsTable.column.organisationUnit'),
      flex: 1,
      sortable: false,
      editable: true,
      renderCell: (params) => (
        <div className="bread-crumb-parent-container">
          {params.value.map((organisationUnit: OrganisationUnit) => (
            <div className="bread-crumb-container" key={`OrganisationUnit_Breadcrumb_${organisationUnit.id}`}>
              {organisationUnit.parentsBreadCrumb && (
                <BreadCrumb
                  showEllipsis={organisationUnit.parentsBreadCrumb.length > 2}
                  allElementNames={organisationUnit.parentsBreadCrumb}
                >
                  {organisationUnit.parentsBreadCrumb.map((parentTitle: string) => {
                    return (
                      <Typography key={`OrganisationUnit_Breadcrumb_${parentTitle}`} variant="caption">
                        {parentTitle}
                      </Typography>
                    );
                  })}
                </BreadCrumb>
              )}
            </div>
          ))}
        </div>
      ),
      renderEditCell: renderEditOrganisationUnit,
    },
  ];

  return (
    <>
      <PageHeader
        title={t('pageHeaderTitle')}
        description={t('pageHeaderDescription')}
        addBtnVisible
        onAdd={handleAddExplanationLink}
        navigationComponent={<SettingTabs />}
        addBtnDisabled={viewingMode === 'editing' || viewingMode === 'creation' || explanationLinksResponseIsLoading}
      />
      {explanationLinksResponseIsLoading && <LoadingIndicator />}
      {explanationLinksResponseIsSuccess && explanationLinks && (
        <DataGrid
          data={explanationLinks}
          columns={columns}
          rowModesModel={rowModesModel}
          editButtons={getActionButtons()}
          overflowMenuOptions={getOverflowMenuItems()}
        />
      )}
      <PopUp
        isOpen={isDeleteConfirmationPopupOpen}
        title={t('deleteExplanationLinkConfirmation.title')}
        body={t('deleteExplanationLinkConfirmation.bodyText')}
        primaryButtonText={t('deleteExplanationLinkConfirmation.confirmButton')}
        secondaryButtonText={t('deleteExplanationLinkConfirmation.cancelButton')}
        handleOnClose={closeDeleteConfirmationPopup}
        secondaryButtonAction={closeDeleteConfirmationPopup}
        primaryButtonAction={() => deleteConfirmationAction()}
        isLoadingPrimaryAction={mutationIsLoading}
      />
    </>
  );
};

export default ExplanationLinks;
